import React, { useEffect, useState, useCallback, useMemo, useContext, useRef } from "react";
import useSWR from "swr";
import {
  Form,
  FormGroup,
  Label,
  Input,
  Card,
  CardBody,
  Row,
  Col,
  CustomInput,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
} from "reactstrap";
import FalconCardHeader from "../common/FalconCardHeader";
import WizardError from "../auth/register/WizardError";
import classNames from "classnames";
import ButtonIcon from "../common/ButtonIcon";
import Datetime from "react-datetime";
import { Controller, useForm, useFieldArray, useWatch } from "react-hook-form";
import NumberFormat from "react-number-format";
import FullPagination from "../common/pagination/FullPagination";
import { Toast } from "../UI/toast/Toast";
import { fetchAccountingAreas, createAccountingArea } from "../../helpers/api/accountingAreas";
import { fetchAccountPlans } from "../../helpers/api/accountPlans";
import { findAllCurrencies, findAllExchangeRates } from "../../helpers/api/currency";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import {
  convertToUsd,
  convertFromUsd,
  formatAmountForBackend,
  formatAmountRounded,
  getCurrencyName,
} from "../../helpers/currencyConversion.js";
import { SUCCESS_RESPONSE } from "../../helpers/codeResponses/codes";
import { faUndo, faSearch, faTimes, faMoneyBillWave, faPercentage, faSave, faCog, faSpinner } from "@fortawesome/free-solid-svg-icons";
import withRedirect from "../../helpers/hoc/withRedirect";
import { useParams, useLocation } from "react-router";
import { PreloadState } from "../UI/preloading/PreloadState";
import { useSelector } from "react-redux";
import { fetchAliquots, fetchSingleSpecialAliquotProfile, saveSingleSpecialAliquotProfile, fetchCreatePeriod } from "../../helpers/api/finance";
import { confirmDuplicatePeriodDates } from "../../helpers/period/confirmDuplicatePeriodDates";
import PeriodSelect from "../common/PeriodSelect";
import { useHistory } from "react-router-dom";
import SelectPropertyListItem from "../common/SelectPropertyListItem";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CurrencyContext } from "../../context/Context";
import InputWithLeftSelect from "../common/CurrencyAmountInput";
import useExchangeRateForDate from "../../helpers/hooks/useExchangeRateForDate";
import CreateExhangeRateModal from "../exchange/components/CreateExchangeRateModal";
import useModuleBase from "../../helpers/hooks/useModuleBase";
import ExpensePaymentFields, { emptyExpensePayment, expensePaymentError, expensePaymentPayload } from "./ExpensePaymentFields";
import { fetchAllPages } from "../../helpers/api/pagination";
import { createProvider } from "../../helpers/api/provider";
import CreateProviderForm from "../providers/CreateProviderForm";

const CreateIndividualCostsForm = ({
  commonCost,
  handleSubmitData,
  handler,
  handlerM,
  setRedirectUrl,
  properties,
  providers,
  mutateProviders,
  common,
  metaa,
  fixedProperty,
  onCancel,
  setParams,
}) => {
  const moduleBase = useModuleBase();

  const { id } = useParams();
  const history = useHistory();
  const { role } = useSelector((stateRedux) => stateRedux.auth.user);
  const { currency: curr, baseCurrency } = useContext(CurrencyContext);
  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    reset,
    watch,
    control,
    formState: { isSubmitting, errors },
  } = useForm({
    mode: "onChange",
    shouldFocusError: true,
    shouldUnregister: false,
    defaultValues: {
      sub_expenses: [{ name: "", value: "" }],
      value: common?.value || "",
      montoOriginal: common?.value ?? "",
      monedaOriginal: common ? baseCurrency.iso : curr?.currency?.id
        ? String(curr.currency.id)
        : "",
      expired_at: common?.expired_at || moment().format("YYYY-MM-DD HH:mm:ss"),
      period_id: common?.period_id ? String(common.period_id) : "",
      provider_id: common?.provider?.id || "",
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "sub_expenses",
  });

  const watchedSubExpenses = watch("sub_expenses");
  const watchedOriginalAmount = useWatch({
    control,
    name: "montoOriginal",
  });
  const watchedOriginalCurrency = useWatch({
    control,
    name: "monedaOriginal",
    defaultValue: "",
  });
  const watchedExpenseDate = useWatch({
    control,
    name: "expired_at",
    defaultValue: common?.expired_at || "",
  });

  const { nextPage, previousPage, perPage, goToPage, meta } = handler || {};

  const next = handlerM?.nextPage;
  const prev = handlerM?.previousPage;
  const per = handlerM?.perPage;
  const goPa = handlerM?.goToPage;
  const met = metaa || {};
  const normalizePropertyId = (value) =>
    value === undefined || value === null ? "" : String(value);
  const toPayloadPropertyId = (value) => {
    const normalizedValue = normalizePropertyId(value);
    return /^\d+$/.test(normalizedValue)
      ? Number(normalizedValue)
      : normalizedValue;
  };

  const [selectedProperties, setSelectedProperties] = useState([]);
  const [selectedPropertiesMap, setSelectedPropertiesMap] = useState({});
  const [selectionMode, setSelectionMode] = useState("manual");
  const [excludedPropertyIds, setExcludedPropertyIds] = useState([]);
  const [distributionType, setDistributionType] = useState("equal");
  const [manualAmounts, setManualAmounts] = useState({});
  const [specialAliquots, setSpecialAliquots] = useState({});
  const [searchText, setSearchText] = useState("");
  const [allAliquots, setAllAliquots] = useState([]);
  const [accountingAreas, setAccountingAreas] = useState([]);
  const [accountPlans, setAccountPlans] = useState([]);
  const periodSelectRef = useRef(null);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [hasLoadedCurrencies, setHasLoadedCurrencies] = useState(false);
  const [hasInitializedCurrencyDefault, setHasInitializedCurrencyDefault] = useState(false);
  const [createAccountAreaModalOpen, setCreateAccountAreaModalOpen] = useState(false);
  const [newAreaName, setNewAreaName] = useState("");
  const [selectedAccountPlanId, setSelectedAccountPlanId] = useState("");
  const [pendingAreaId, setPendingAreaId] = useState(null);
  const [createProviderModalOpen, setCreateProviderModalOpen] = useState(false);
  const [createdProviders, setCreatedProviders] = useState([]);
  const [pendingProviderId, setPendingProviderId] = useState(null);

  const providerOptions = useMemo(() => {
    const list = providers || [];
    const missing = createdProviders.filter(
      (created) => !list.some((provider) => provider.id === created.id)
    );
    return [...list, ...missing];
  }, [providers, createdProviders]);

  // El endpoint trae TODAS las áreas contables del condominio (de gastos y de
  // ingresos mezcladas); acá solo dejamos pasar las que apuntan a una cuenta
  // de Gastos (grupo "6"), para no mostrar áreas creadas desde el flujo de
  // ingresos.
  const expenseAccountingAreas = accountingAreas.filter((area) => {
    const code = area.account_plan?.code || area.accountPlan?.code;
    return code && code.startsWith("6");
  });
  const [createPeriodModalOpen, setCreatePeriodModalOpen] = useState(false);
  const [newPeriodIsCustom, setNewPeriodIsCustom] = useState(false);
  const [newPeriodName, setNewPeriodName] = useState("");
  const [newPeriodStartDate, setNewPeriodStartDate] = useState("");
  const [newPeriodEndDate, setNewPeriodEndDate] = useState("");
  const [creatingPeriod, setCreatingPeriod] = useState(false);

  const periodMonthNames = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
  ];
  const periodCurrentMonth = moment().month();
  const periodCurrentYear = moment().year();

  const {
    condominium: { selectedCondominium },
  } = useSelector((state) => state);
  const condominiumId = id || selectedCondominium;
  const activeExchangeRate = Number(curr?.value ?? 0);

  const { state } = useLocation();

  const currencyCatalog = useMemo(() => {
    const normalized = (availableCurrencies || []).map((item) => ({
      ...item,
      id: String(item.id),
      iso: (item?.iso || "").toUpperCase(),
      isFallbackUsd: false,
    }));

    const hasUsd = normalized.some((item) => item.iso === baseCurrency.iso);
    if (!hasUsd) {
      normalized.push({
        id: baseCurrency.iso,
        iso: baseCurrency.iso,
        name: getCurrencyName(baseCurrency.iso),
        isFallbackUsd: true,
      });
    }

    return normalized;
  }, [availableCurrencies, baseCurrency.iso]);

  const preferredDefaultCurrency = useMemo(() => {
    const bolivaresCurrency = currencyCatalog.find((item) => {
      const name = (item?.name || "").toLowerCase();
      const iso = (item?.iso || "").toLowerCase();
      const symbol = (item?.symbol || "").toLowerCase();

      return name.includes("boliva") || iso === "ves" || symbol === "bs" || iso === "bs";
    });

    const fallbackByBase = currencyCatalog.find(
      (item) => (item?.iso || "").toUpperCase() === baseCurrency.iso
    );

    return bolivaresCurrency || fallbackByBase || currencyCatalog[0] || null;
  }, [baseCurrency.iso, currencyCatalog]);

  const selectedCurrencyData = useMemo(() => {
    const currentValue = String(watchedOriginalCurrency || "");
    return (currencyCatalog || []).find(
      (item) =>
        String(item.id) === currentValue ||
        item.iso === currentValue.toUpperCase()
    );
  }, [currencyCatalog, watchedOriginalCurrency]);

  const isSelectedCurrencyUsd =
    (selectedCurrencyData?.iso || "").toUpperCase() === baseCurrency.iso;

  const parsedExpenseDate = useMemo(() => {
    if (!watchedExpenseDate) {
      return null;
    }

    const parsedDate = moment(watchedExpenseDate);
    return parsedDate.isValid() ? parsedDate : null;
  }, [watchedExpenseDate]);

  const totalValueWatch = watch("montoOriginal");
  const [payment, setPayment] = useState(emptyExpensePayment);
  const selectedPropertiesDetails = useMemo(
    () =>
      selectedProperties.map(
        (propertyId) =>
          selectedPropertiesMap[propertyId] || {
            id: propertyId,
          }
      ),
    [selectedProperties, selectedPropertiesMap]
  );

  const totalPropertiesAvailable = Number(
    met?.total || meta?.total || 0
  );
  const isAllSelectionMode = selectionMode === "all";
  const selectedPropertiesCount = isAllSelectionMode
    ? Math.max(totalPropertiesAvailable - excludedPropertyIds.length, 0)
    : selectedProperties.length;
  const hasSelectedProperties = selectedPropertiesCount > 0;
  const isAllPropertiesSelected =
    isAllSelectionMode && totalPropertiesAvailable > 0;

  const isPropertySelected = (propertyId) => {
    const normalizedPropertyId = normalizePropertyId(propertyId);

    if (isAllSelectionMode) {
      return !excludedPropertyIds.includes(normalizedPropertyId);
    }

    return selectedProperties.includes(normalizedPropertyId);
  };

  useEffect(() => {
    let isMounted = true;
    const loadSpecialProfile = async () => {
      try {
        const response = await fetchSingleSpecialAliquotProfile(id);
        if (isMounted && response && response.data && response.data.aliquots) {
          setSpecialAliquots(response.data.aliquots || {});
        }
      } catch (error) {
        console.error("Error loading special aliquot profile", error);
      }
    };
    if (id) {
      loadSpecialProfile();
    }
    return () => {
      isMounted = false;
    };
  }, [id]);

  const handleReloadSpecialProfile = async () => {
    try {
      const response = await fetchSingleSpecialAliquotProfile(id);
      if (response && response.data && response.data.aliquots) {
        setSpecialAliquots(response.data.aliquots || {});
        Toast.fire({
          type: "success",
          title: "Configuración especial recargada",
        });
      } else {
        Toast.fire({
          type: "info",
          title: "No existe una configuración especial guardada",
        });
      }
    } catch (error) {
      console.error("Error reloading special profile", error);
    }
  };

  // useSWR para obtener datos de forma eficiente y evitar duplicados
  const { data: areasRes, mutate: mutateAreas } = useSWR(id ? [id, "accounting-areas"] : null, () => fetchAccountingAreas(id));
  const { data: plansRes } = useSWR(id ? [id, "account-plans"] : null, () => fetchAccountPlans(id));
  const { data: aliquotsRes } = useSWR(id ? [id, "aliquots"] : null, () => fetchAllPages((page, pageSize) => fetchAliquots(page, pageSize, { condominiumId: id })));

  useEffect(() => {
    if (areasRes && areasRes.data) setAccountingAreas(areasRes.data);
  }, [areasRes]);

  useEffect(() => {
    if (pendingAreaId && expenseAccountingAreas.some((area) => area.id === pendingAreaId)) {
      setValue("accounting_area_id", pendingAreaId, { shouldValidate: true });
      setPendingAreaId(null);
    }
  }, [accountingAreas, pendingAreaId, setValue]);

  useEffect(() => {
    if (pendingProviderId && providerOptions.some((provider) => provider.id === pendingProviderId)) {
      setValue("provider_id", String(pendingProviderId), { shouldValidate: true });
      setPendingProviderId(null);
    }
  }, [providerOptions, pendingProviderId, setValue]);

  useEffect(() => {
    if (plansRes && plansRes.data) setAccountPlans(plansRes.data);
  }, [plansRes]);

  useEffect(() => {
    if (aliquotsRes && aliquotsRes.data) setAllAliquots(aliquotsRes.data);
  }, [aliquotsRes]);

  useEffect(() => {
    let isMounted = true;

    const loadCurrencies = async () => {
      if (!condominiumId) return;

      setHasLoadedCurrencies(false);

      try {
        const response = await fetchAllPages((page, pageSize) => findAllCurrencies(page, pageSize, {
          condominium_id: condominiumId,
        }));

        if (isMounted) {
          setAvailableCurrencies(response?.data || []);
        }
      } catch (error) {
        if (isMounted) {
          handleErrorMessage(error);
        }
      } finally {
        if (isMounted) {
          setHasLoadedCurrencies(true);
        }
      }
    };

    loadCurrencies();

    return () => {
      isMounted = false;
    };
  }, [condominiumId]);

  useEffect(() => {
    if (!hasLoadedCurrencies || hasInitializedCurrencyDefault || !preferredDefaultCurrency) {
      return;
    }

    const preferredCurrencyId = String(preferredDefaultCurrency.id);
    const currentCurrencyId = String(watchedOriginalCurrency || "");

    if (currentCurrencyId !== preferredCurrencyId) {
      setValue("monedaOriginal", preferredCurrencyId, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }

    setHasInitializedCurrencyDefault(true);
  }, [
    baseCurrency.iso,
    hasInitializedCurrencyDefault,
    hasLoadedCurrencies,
    preferredDefaultCurrency,
    setValue,
    watchedOriginalCurrency,
  ]);

  useEffect(() => {
    if (!hasLoadedCurrencies || !currencyCatalog.length) {
      return;
    }

    if (hasInitializedCurrencyDefault || watchedOriginalCurrency) {
      return;
    }

    const fallback = preferredDefaultCurrency;

    if (fallback) {
      setValue("monedaOriginal", String(fallback.id), {
        shouldDirty: false,
        shouldValidate: true,
      });
      setHasInitializedCurrencyDefault(true);
    }
  }, [
    currencyCatalog,
    hasLoadedCurrencies,
    preferredDefaultCurrency,
    setValue,
    watchedOriginalCurrency,
    hasInitializedCurrencyDefault,
  ]);

  const [isRateModalOpen, setIsRateModalOpen] = useState(false);
  const canRegisterRate = role === "manager" || role === "root";

  const {
    rateByDate,
    rateResolution,
    isRateLoading,
    appliedExchangeRate,
    bumpRateRefreshTick,
  } = useExchangeRateForDate({
    currencyId: selectedCurrencyData?.id,
    isUsdCurrency: isSelectedCurrencyUsd || Boolean(selectedCurrencyData?.isFallbackUsd),
    date: parsedExpenseDate,
    activeExchangeRate,
    condominiumId: condominiumId || selectedCondominium,
  });

  // HO-TASK-253: la tasa en moneda local (Bs) de la fecha del gasto, para
  // guardarla como `rate` y mostrar el equivalente aunque el monto se cargue
  // en dólares. Si la moneda elegida ya es la local, la resuelve el hook de
  // arriba y este se apaga (currencyId nulo) para no pedir ni registrar la
  // misma tasa dos veces.
  const localCurrencyId = curr?.currency?.id;
  const isSelectedCurrencyLocal =
    Boolean(localCurrencyId) && String(selectedCurrencyData?.id) === String(localCurrencyId);
  const { appliedExchangeRate: localRateByDate, isRateLoading: isLocalRateLoading } =
    useExchangeRateForDate({
      currencyId: isSelectedCurrencyLocal ? null : localCurrencyId,
      isUsdCurrency: false,
      date: parsedExpenseDate,
      activeExchangeRate: null,
      condominiumId: condominiumId || selectedCondominium,
    });
  const rateForExpenseDate = isSelectedCurrencyLocal ? appliedExchangeRate : localRateByDate;
  const paymentAmount = useMemo(() => {
    const converted = Number(convertToUsd(totalValueWatch, isSelectedCurrencyUsd, appliedExchangeRate) || 0);
    return distributionType === "fixed" ? converted * selectedPropertiesCount : converted;
  }, [appliedExchangeRate, distributionType, isSelectedCurrencyUsd, selectedPropertiesCount, totalValueWatch]);

  const isSelectedDateToday =
    Boolean(parsedExpenseDate) && parsedExpenseDate.format("YYYY-MM-DD") === moment().format("YYYY-MM-DD");

  const getAccountingData = async () => {
    // Para recarga manual
    mutateAreas();
  };

  const handleCreateAccountingArea = async () => {
    if (!newAreaName) return;
    try {
      let planId = selectedAccountPlanId;
      if (!planId) {
        const defaultPlan = (accountPlans || []).find(p => p.code === '6.1');
        planId = defaultPlan ? defaultPlan.id : null;
      }

      const response = await createAccountingArea({
        condominium_id: id,
        name: newAreaName,
        account_plan_id: planId,
      });
      if (response.code === SUCCESS_RESPONSE.OK || response.code === SUCCESS_RESPONSE.CREATED) {
        Toast.fire({
          type: "success",
          title: "Área creada con éxito",
        });
        setNewAreaName("");
        setSelectedAccountPlanId("");
        setCreateAccountAreaModalOpen(false);
        setPendingAreaId(response.data?.id ?? null);
        mutateAreas();
      }
    } catch (error) {
      handleErrorMessage(error);
    }
  };

  const handleCreateProviderSubmit = async (values) => {
    try {
      const response = await createProvider(condominiumId, values);
      const createdProvider = response?.data;
      const isSuccess =
        response?.code === SUCCESS_RESPONSE.CREATED ||
        response?.code === SUCCESS_RESPONSE.OK ||
        createdProvider?.id;

      if (isSuccess) {
        setCreateProviderModalOpen(false);
        Toast.fire({
          type: "success",
          title: "Se ha creado el proveedor satisfactoriamente.",
        });
        if (createdProvider?.id) {
          setCreatedProviders((prev) => [...prev, createdProvider]);
          setPendingProviderId(createdProvider.id);
        }
        if (mutateProviders) {
          mutateProviders();
        }
      }
    } catch (error) {
      handleErrorMessage(error);
    }
  };

  const handleOpenCreatePeriodModal = () => {
    setNewPeriodIsCustom(false);
    const monthName = periodMonthNames[periodCurrentMonth];
    setNewPeriodName(`${monthName} ${periodCurrentYear}`);
    const start = moment().month(periodCurrentMonth).startOf("month").format("YYYY-MM-DD");
    const end = moment().month(periodCurrentMonth).endOf("month").format("YYYY-MM-DD");
    setNewPeriodStartDate(start);
    setNewPeriodEndDate(end);
    setCreatePeriodModalOpen(true);
  };

  const handlePeriodModalTypeChange = (e) => {
    const isCustom = e.target.value === "custom";
    setNewPeriodIsCustom(isCustom);
    if (!isCustom) {
      const monthName = periodMonthNames[periodCurrentMonth];
      setNewPeriodName(`${monthName} ${periodCurrentYear}`);
      const start = moment().month(periodCurrentMonth).startOf("month").format("YYYY-MM-DD");
      const end = moment().month(periodCurrentMonth).endOf("month").format("YYYY-MM-DD");
      setNewPeriodStartDate(start);
      setNewPeriodEndDate(end);
    } else {
      setNewPeriodName("");
    }
  };

  const handlePeriodModalMonthChange = (e) => {
    const selectedValue = e.target.value;
    setNewPeriodName(selectedValue);
    const monthName = selectedValue.split(" ")[0];
    const monthIndex = periodMonthNames.indexOf(monthName);
    if (monthIndex !== -1) {
      const start = moment().year(periodCurrentYear).month(monthIndex).startOf("month").format("YYYY-MM-DD");
      const end = moment().year(periodCurrentYear).month(monthIndex).endOf("month").format("YYYY-MM-DD");
      setNewPeriodStartDate(start);
      setNewPeriodEndDate(end);
    }
  };

  const submitNewPeriod = async (extra = {}) => {
    const response = await fetchCreatePeriod(condominiumId, {
      name: newPeriodName,
      start_at: newPeriodStartDate,
      end_at: newPeriodEndDate,
      ...extra,
    });
    if (response.code === SUCCESS_RESPONSE.OK || response.code === SUCCESS_RESPONSE.CREATED) {
      Toast.fire({ type: "success", title: "Período creado exitosamente" });
      // Vuelve a la página 1 (orderBy id DESC en el backend: el recién creado queda primero) y
      // lo selecciona por nombre en la lista fresca, en vez de confiar en lo que ya había cargado.
      const freshPeriods = (await periodSelectRef.current?.reload("")) || [];
      const newPeriod = freshPeriods.find((p) => p.name === newPeriodName);
      if (newPeriod) {
        setValue("period_id", newPeriod.id);
      }
      setNewPeriodName("");
      setNewPeriodStartDate("");
      setNewPeriodEndDate("");
      setCreatePeriodModalOpen(false);
    }
  };

  const handleCreatePeriod = async () => {
    if (!newPeriodName || !newPeriodStartDate || !newPeriodEndDate) {
      Toast.fire({ type: "error", title: "Completa todos los campos del período" });
      return;
    }
    setCreatingPeriod(true);
    try {
      await submitNewPeriod();
    } catch (error) {
      const handled = await confirmDuplicatePeriodDates(error, () =>
        submitNewPeriod({ confirm_duplicate_dates: true }).catch(handleErrorMessage)
      );
      if (!handled) handleErrorMessage(error);
    } finally {
      setCreatingPeriod(false);
    }
  };

  useEffect(() => {
    if (isAllSelectionMode) {
      setValue("property_id", null);
      if (hasSelectedProperties) {
        clearErrors("properties");
      }
      return;
    }

    if (selectedProperties.length > 0) {
      if (selectedProperties.length === 1) {
        setValue("property_id", toPayloadPropertyId(selectedProperties[0]));
      } else {
        setValue("property_id", null);
      }
      clearErrors("properties");
    }
  }, [
    clearErrors,
    hasSelectedProperties,
    isAllSelectionMode,
    selectedProperties,
    setValue,
  ]);

  useEffect(() => {
    if (
      isAllSelectionMode &&
      (distributionType === "manual" || distributionType === "special_aliquot")
    ) {
      setDistributionType("equal");
    }
  }, [distributionType, isAllSelectionMode]);

  useEffect(() => {
    setRedirectUrl(`/dashboard/providers`);
  }, [setRedirectUrl]);

  useEffect(() => {
    if (!properties?.length || !selectedProperties.length) {
      return;
    }

    setSelectedPropertiesMap((prev) => {
      const nextMap = { ...prev };

      properties.forEach((property) => {
        const propertyId = normalizePropertyId(property?.id);

        if (propertyId && selectedProperties.includes(propertyId)) {
          nextMap[propertyId] = property;
        }
      });

      return nextMap;
    });
  }, [properties, selectedProperties]);

  useEffect(() => {
    if (common) {
      const providerId = normalizePropertyId(common.provider.id);
      setSelectedProperties(providerId ? [providerId] : []);
      setSelectionMode("manual");
      setExcludedPropertyIds([]);
      if (providerId) {
        setSelectedPropertiesMap((prev) => ({
          ...prev,
          [providerId]: common.provider,
        }));
      }
      setValue("value", common.value);
      setValue("montoOriginal", common?.value ?? "");
      setValue("monedaOriginal", baseCurrency.iso);
      // setDescription(common.description);
      // setDistributionType ?
    } else if (fixedProperty) {
      const fixedPropertyId = normalizePropertyId(fixedProperty.id);
      setSelectedProperties(fixedPropertyId ? [fixedPropertyId] : []);
      setSelectionMode("manual");
      setExcludedPropertyIds([]);
      if (fixedPropertyId) {
        setSelectedPropertiesMap((prev) => ({
          ...prev,
          [fixedPropertyId]: fixedProperty,
        }));
      }
    }
    // eslint-disable-next-line
  }, [baseCurrency.iso, common, fixedProperty, setValue]);


  useEffect(() => {
    if (!state?.type_id || !state?.type_name) {
      history.push(`/dashboard/finance`);
    }
  }, [state]);

  useEffect(() => {
    const defaultValues = {};
    defaultValues.name = common?.name;
    defaultValues.description = common?.description;
    defaultValues.type_payment_supplier = common?.type_payment_supplier;
    defaultValues.value = common?.value;
    defaultValues.montoOriginal = common?.value;
    defaultValues.monedaOriginal = common ? baseCurrency.iso : curr?.currency?.id
      ? String(curr.currency.id)
      : "";
    defaultValues.expired_at = common?.expired_at || moment().format("YYYY-MM-DD HH:mm:ss");
    defaultValues.provider_id = common?.provider?.id || "";
    // No pisar la selección de período que ya haya hecho el usuario: `common`
    // solo trae period_id en modo edición, así que en modo creación se
    // preserva lo que haya en el form al momento de correr este efecto.
    defaultValues.period_id = common?.period_id ? String(common.period_id) : watch("period_id");

    reset({ ...defaultValues });
    // eslint-disable-next-line
  }, [baseCurrency.iso, common, curr, reset]);

  useEffect(() => {
    if (!hasLoadedCurrencies || !common || !currencyCatalog.length) {
      return;
    }

    const currentMoneda = String(watch("monedaOriginal") || "");
    const preferredCurrencyId = String(preferredDefaultCurrency?.id || "");

    if (preferredCurrencyId && currentMoneda !== preferredCurrencyId) {
      setValue("monedaOriginal", preferredCurrencyId, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [common, currencyCatalog, hasLoadedCurrencies, preferredDefaultCurrency, setValue, watch]);

  useEffect(() => {
    if (!hasLoadedCurrencies || !common || !preferredDefaultCurrency) {
      return;
    }

    if (!selectedCurrencyData) {
      return;
    }

    const preferredCurrencyId = String(preferredDefaultCurrency.id);
    const currentCurrencyId = String(selectedCurrencyData.id || "");

    if (currentCurrencyId !== preferredCurrencyId) {
      return;
    }

    if (!common?.value) {
      return;
    }

    if ((preferredDefaultCurrency?.iso || "").toUpperCase() === baseCurrency.iso) {
      setValue("montoOriginal", common.value, {
        shouldDirty: false,
        shouldValidate: true,
      });
      return;
    }

    if (!appliedExchangeRate || appliedExchangeRate <= 0) {
      return;
    }

    if (!isSelectedCurrencyUsd && parsedExpenseDate && !rateResolution?.selectedDateKey) {
      return;
    }

    setValue(
      "montoOriginal",
      String(convertFromUsd(common.value, false, appliedExchangeRate)),
      {
        shouldDirty: false,
        shouldValidate: true,
      }
    );
  }, [
    appliedExchangeRate,
    baseCurrency.iso,
    common,
    hasLoadedCurrencies,
    isSelectedCurrencyUsd,
    parsedExpenseDate,
    preferredDefaultCurrency,
    rateResolution,
    selectedCurrencyData,
    setValue,
  ]);

  const handlePropertySelect = (property) => {
    if (fixedProperty) return;
    const propertyId = normalizePropertyId(property.id);

    if (!propertyId) {
      return;
    }

    setSelectedPropertiesMap((prev) => ({
      ...prev,
      [propertyId]: property,
    }));

    if (isAllSelectionMode) {
      setExcludedPropertyIds((prev) => {
        if (prev.includes(propertyId)) {
          return prev.filter((id) => id !== propertyId);
        }

        return [...prev, propertyId];
      });
      return;
    }

    setSelectedProperties((prev) => {
      if (prev.includes(propertyId)) {
        const newSelection = prev.filter((id) => id !== propertyId);

        const newManualAmounts = { ...manualAmounts };
        delete newManualAmounts[propertyId];
        setManualAmounts(newManualAmounts);

        // We do NOT delete special aliquot values when deselecting, to persist them better?
        // Or maybe we do. If single profile, maybe we keep them in memory `specialAliquots` 
        // even if not selected, so if he reselects, it is there?
        // Let's keep them in state `specialAliquots`.

        return newSelection;
      } else {
        return [...prev, propertyId];
      }
    });
  };

  const handleToggleSelectAllProperties = () => {
    if (fixedProperty) {
      return;
    }

    if (isAllSelectionMode) {
      setSelectionMode("manual");
      setExcludedPropertyIds([]);
      setSelectedProperties([]);
      setManualAmounts({});
      clearErrors("properties");
      return;
    }

    setSelectionMode("all");
    setSelectedProperties([]);
    setExcludedPropertyIds([]);
    clearErrors("properties");

    Toast.fire({
      type: "success",
      title: "Selección global activada",
      text: "Se aplicará a todos los propietarios del filtro actual.",
    });
  };

  const handleManualAmountChange = (propertyId, value) => {
    setManualAmounts((prev) => ({
      ...prev,
      [propertyId]: parseFloat(value),
    }));
  };

  const handleSpecialAliquotChange = (propertyId, value) => {
    setSpecialAliquots((prev) => ({
      ...prev,
      [propertyId]: parseFloat(value),
    }));
  };

  const handleLoadStoredAliquots = () => {
    const newSpecialAliquots = { ...specialAliquots };
    let loadedCount = 0;

    selectedProperties.forEach(propId => {
      const match = allAliquots.find(
        (a) =>
          a.property &&
          normalizePropertyId(a.property.id) === normalizePropertyId(propId)
      );
      if (match) {
        newSpecialAliquots[normalizePropertyId(propId)] = match.value;
        loadedCount++;
      }
    });

    setSpecialAliquots(newSpecialAliquots);

    Toast.fire({
      type: "success",
      title: "Porcentajes cargados desde el registro maestro (Normales)",
    });
  };

  const applyPropertySearch = (value = searchText) => {
    if (!setParams || fixedProperty) return;
    const term = typeof value === "string" ? value.trim() : "";
    setSearchText(term);
    setParams(term);
  };

  const filteredProperties = properties;

  const renderPropertiesItems = () =>
    filteredProperties.map((property) => {
      const propertyId = normalizePropertyId(property.id);
      const isSelected = isPropertySelected(propertyId);
      return (
        <div key={propertyId} className="mb-2">
          <div
            className={`p-3 rounded border cursor-pointer transition-base ${isSelected
              ? "bg-soft-primary border-primary"
              : "bg-light border-200"
              }`}
            onClick={() => handlePropertySelect(property)}
            style={{ cursor: "pointer" }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handlePropertySelect(property);
              }
            }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h6
                  className={`mb-0 ${isSelected ? "text-primary" : "text-700"}`}
                >
                  {property?.user?.username} | {property?.user?.data?.firstname}{" "}
                  {property?.user?.data?.lastname}
                </h6>
                <small className="text-secondary">
                  {property?.user?.data?.dni || "Sin DNI"}
                </small>
                {/* HO-ISSUE-120: la lista mostraba usuario, nombre y DNI, y el
                inmueble --que es por donde el administrador ubica la unidad--
                no aparecía. Sin cargar se dice, para que se note a quién le
                falta antes de cargarle el gasto. */}
                <small
                  className={`d-block ${
                    String(property?.property || "").trim()
                      ? "text-600"
                      : "text-warning"
                  }`}
                >
                  Inmueble:{" "}
                  {String(property?.property || "").trim() || "sin asignar"}
                </small>
              </div>
              {isSelected && (
                <FontAwesomeIcon icon="check-circle" className="text-primary" />
              )}
            </div>
          </div>
        </div>
      );
    });

  const renderManualAmounts = () => {
    if ((distributionType !== "manual" && distributionType !== "special_aliquot") || selectedProperties.length === 0)
      return null;

    const isAliquot = distributionType === "special_aliquot";

    return (
      <div className="bg-white border rounded p-3 mt-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <h6 className="text-primary mb-2 mb-md-0">
            <FontAwesomeIcon icon={isAliquot ? faPercentage : faMoneyBillWave} className="mr-2" />
            {isAliquot ? "Distribución por Alícuota Especial " : "Definir montos específicos por propiedad"}
          </h6>

          {isAliquot && (
            <div className="d-flex align-items-center">
              <Button
                size="sm"
                className="mr-2 text-white"
                style={{ backgroundColor: '#6f42c1', borderColor: '#6f42c1' }}
                onClick={handleReloadSpecialProfile}
                title="Cargar los porcentajes especiales configurados previamente para estos propietarios"
              >
                <FontAwesomeIcon icon={faPercentage} className="mr-1" />
                Cargar Alícuotas Especiales
              </Button>
            </div>
          )}
        </div>

        <div style={{ maxHeight: "300px", overflowY: "auto" }}>
          {selectedPropertiesDetails.map((property) => {
            const propertyId = normalizePropertyId(property.id);
            const calculatedAmount = isAliquot && totalValueWatch ? (totalValueWatch * (specialAliquots[propertyId] || 0) / 100) : 0;
            return (
              <Row
                key={propertyId}
                className="align-items-center mb-2 mx-0 py-2 border-bottom border-100"
              >
                <Col xs={7} className="pl-0">
                  <div className="font-weight-semi-bold text-nowrap text-truncate">
                    {property?.user?.username || `Propiedad #${propertyId}`}
                  </div>
                  <div className="fs--2 text-600 text-truncate">
                    {(property?.user?.data?.firstname || "Sin nombre")}{" "}
                    {property?.user?.data?.lastname || ""}
                  </div>
                </Col>
                <Col xs={5} className="pr-0">
                  <NumberFormat
                    customInput={Input}
                    bsSize="sm"
                    thousandSeparator
                    prefix={isAliquot ? "" : `${selectedCurrencyData?.iso || "$"} `}
                    suffix={isAliquot ? " %" : ""}
                    placeholder="0.00"
                    onValueChange={(values) => {
                      if (isAliquot) {
                        handleSpecialAliquotChange(propertyId, values.value);
                      } else {
                        handleManualAmountChange(propertyId, values.value);
                      }
                    }}
                    value={(isAliquot ? specialAliquots[propertyId] : manualAmounts[propertyId]) || ""}
                    className="text-right font-weight-bold"
                  />
                  {isAliquot && totalValueWatch > 0 && (
                    <div className="text-right fs--2 text-600 mt-1">
                      ~ {`${selectedCurrencyData?.iso || "$"} ${calculatedAmount.toFixed(2)}`}
                    </div>
                  )}
                </Col>
              </Row>
            )
          })}
        </div>
        <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
          <span className="font-weight-bold">{isAliquot ? "Total Porcentaje:" : "Total Calculado:"}</span>
          {isAliquot ? (
            <span className={`font-weight-bold fs-1 ${Math.abs(selectedProperties.reduce((acc, curr) => acc + (specialAliquots[curr] || 0), 0) - 100) < 0.05 ? "text-success" : "text-danger"}`}>
              {selectedProperties.reduce((acc, curr) => acc + (specialAliquots[curr] || 0), 0).toFixed(4) + " %"}
            </span>
          ) : (
            <span className="font-weight-bold fs-1">
              {`${selectedCurrencyData?.iso || "$"} ` + selectedProperties
                .reduce((acc, curr) => acc + (manualAmounts[curr] || 0), 0)
                .toFixed(2)}
            </span>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    const convertedValue = convertToUsd(
      watchedOriginalAmount,
      isSelectedCurrencyUsd,
      appliedExchangeRate
    );

    setValue("value", convertedValue === "" ? "" : String(convertedValue));
  }, [appliedExchangeRate, isSelectedCurrencyUsd, setValue, watchedOriginalAmount]);

  const resolveRateForCurrency = async (currencyData) => {
    //
    if (!currencyData) {
      return null;
    }

    const isUsdCurrency =
      (currencyData?.iso || "").toUpperCase() === baseCurrency.iso ||
      currencyData?.isFallbackUsd;

    if (isUsdCurrency) {
      return 1;
    }

    try {
      // 
      const response = await fetchAllPages((page, pageSize) => findAllExchangeRates(page, pageSize, {
        currency: currencyData.id,
      }));

      const rates = response?.data || [];
      const selectedDateKey = parsedExpenseDate?.format("YYYY-MM-DD");
      const rateForDay = selectedDateKey
        ? rates
          .filter(
            (rate) =>
              moment(rate.date || rate.created_at).format("YYYY-MM-DD") ===
              selectedDateKey
          )
          .sort(
            (a, b) =>
              moment(b.date || b.created_at).valueOf() -
              moment(a.date || a.created_at).valueOf()
          )[0]
        : null;

      if (selectedDateKey && !rateForDay) {
        return 0;
      }

      return Number(rateForDay?.value || appliedExchangeRate || activeExchangeRate || 0);
    } catch (error) {
      return Number(appliedExchangeRate || activeExchangeRate || 0);
    }
  };

  const handleCurrencyChange = async (event) => {
    const nextCurrency = event.target.value;
    const nextCurrencyData = (currencyCatalog || []).find(
      (item) => String(item.id) === String(nextCurrency)
    );
    const currentCurrencyData = selectedCurrencyData;

    if (!nextCurrencyData || !currentCurrencyData) {
      setValue("monedaOriginal", nextCurrency, {
        shouldDirty: true,
        shouldValidate: true,
      });
      return;
    }

    const currentIsUsd =
      (currentCurrencyData?.iso || "").toUpperCase() === baseCurrency.iso;
    const nextIsUsd =
      (nextCurrencyData?.iso || "").toUpperCase() === baseCurrency.iso;

    if (!currentIsUsd && !nextIsUsd) {
      setValue("monedaOriginal", nextCurrency, {
        shouldDirty: true,
        shouldValidate: true,
      });
      return;
    }

    let nextOriginalAmount = watchedOriginalAmount;

    if (currentIsUsd && !nextIsUsd) {
      const targetRate = await resolveRateForCurrency(nextCurrencyData);

      if (!targetRate || targetRate <= 0) {
        Toast.fire({
          type: "error",
          title: "No hay una tasa del dia disponible para convertir la moneda seleccionada.",
        });
        return;
      }

      nextOriginalAmount = convertFromUsd(
        watchedOriginalAmount,
        false,
        targetRate
      );
    } else if (!currentIsUsd && nextIsUsd) {
      const sourceRate =
        Number(appliedExchangeRate || 0) > 0 && !rateResolution?.isFallback
          ? Number(appliedExchangeRate)
          : await resolveRateForCurrency(currentCurrencyData);

      if (!sourceRate || sourceRate <= 0) {
        Toast.fire({
          type: "error",
          title: "No hay una tasa del dia disponible para convertir la moneda seleccionada.",
        });
        return;
      }

      nextOriginalAmount = convertToUsd(
        watchedOriginalAmount,
        false,
        sourceRate
      );
    }

    setValue("monedaOriginal", nextCurrency, {
      shouldDirty: true,
      shouldValidate: true,
    });

    setValue(
      "montoOriginal",
      nextOriginalAmount === "" ? "" : String(nextOriginalAmount),
      {
        shouldDirty: true,
        shouldValidate: true,
      }
    );
  };

  const usdEquivalent = useMemo(() => {
    if (isSelectedCurrencyUsd) {
      return null;
    }

    const convertedAmount = convertToUsd(
      watchedOriginalAmount,
      false,
      appliedExchangeRate
    );

    return convertedAmount === "" ? null : convertedAmount;
  }, [appliedExchangeRate, isSelectedCurrencyUsd, watchedOriginalAmount]);

  const helpText = useMemo(() => {
    if (!parsedExpenseDate || !parsedExpenseDate.isValid()) {
      return "Selecciona la fecha primero";
    }

    const dateLabel = parsedExpenseDate.format("DD/MM/YYYY");

    // HO-TASK-253: cargado en dólares, el equivalente se muestra en la moneda
    // local a la tasa de la fecha del gasto, que es la que se guarda.
    if (isSelectedCurrencyUsd) {
      if (isLocalRateLoading || !curr?.currency?.iso) {
        return "";
      }

      const localEquivalent = convertFromUsd(watchedOriginalAmount, false, rateForExpenseDate);

      if (localEquivalent === "") {
        return "";
      }

      return `Tasa ${dateLabel}: ${formatAmountRounded(rateForExpenseDate, 2)} | Equivalente aproximado: ${curr.currency.iso} ${formatAmountRounded(localEquivalent, 2)}`;
    }

    if (isRateLoading) {
      return "";
    }

    if (usdEquivalent === null) {
      return "";
    }

    const rateLabel = appliedExchangeRate
      ? formatAmountRounded(appliedExchangeRate, 2)
      : "N/D";

    return `Tasa ${dateLabel}: ${rateLabel} | Equivalente aproximado: ${baseCurrency.symbol}${formatAmountRounded(usdEquivalent, 2)} ${baseCurrency.iso}`;
  }, [
    appliedExchangeRate,
    isRateLoading,
    isLocalRateLoading,
    rateForExpenseDate,
    watchedOriginalAmount,
    curr,
    isSelectedCurrencyUsd,
    parsedExpenseDate,
    rateResolution,
    usdEquivalent,
    baseCurrency,
  ]);

  const onSubmit = async (data) => {

    if (!hasSelectedProperties) {
      Toast.fire({
        type: "error",
        title: "Debes seleccionar al menos un propietario",
      });
      return;
    }

    if (
      isAllSelectionMode &&
      (distributionType === "manual" || distributionType === "special_aliquot")
    ) {
      Toast.fire({
        type: "error",
        title: "Distribución no disponible",
        text: "Con selección global solo se permite distribución equitativa o cobro fijo.",
      });
      return;
    }

    if (!data.expired_at) {
      Toast.fire({
        type: "error",
        title: "La fecha del gasto es obligatoria.",
      });
      return;
    }

    if (fixedProperty && !data.period_id) {
      Toast.fire({
        type: "error",
        title: "Debes seleccionar un período para asignar el saldo.",
      });
      return;
    }

    if (!isSelectedCurrencyUsd && (!appliedExchangeRate || appliedExchangeRate <= 0 || rateResolution?.isFallback)) {
      Toast.fire({
        type: "error",
        title: "No hay una tasa del dia disponible para convertir esta moneda a USD.",
      });
      return;
    }

    const normalizedUsdValue = convertToUsd(
      data.montoOriginal,
      isSelectedCurrencyUsd,
      appliedExchangeRate
    );

    if (normalizedUsdValue === "") {
      Toast.fire({
        type: "error",
        title: "Monto inválido para convertir a USD.",
      });
      return;
    }

    let items = [];
    const totalValue = parseFloat(normalizedUsdValue || 0);

    if (!isAllSelectionMode) {
      if (distributionType === "equal") {
        const amountPerProp = totalValue / selectedProperties.length;
        items = selectedProperties.map((id) => ({
          property_id: toPayloadPropertyId(id),
          amount: amountPerProp,
        }));
      } else if (distributionType === "fixed") {
        items = selectedProperties.map((id) => ({
          property_id: toPayloadPropertyId(id),
          amount: totalValue,
        }));
        data.value = totalValue * selectedProperties.length;
      } else if (distributionType === "manual") {
        items = selectedProperties.map((id) => ({
          property_id: toPayloadPropertyId(id),
          amount: Number(
            convertToUsd(
              manualAmounts[id] || 0,
              isSelectedCurrencyUsd,
              appliedExchangeRate
            ) || 0
          ),
        }));
        const sum = items.reduce((acc, curr) => acc + curr.amount, 0);
        data.value = sum;
      } else if (distributionType === "special_aliquot") {
        const totalPercentage = selectedProperties.reduce((acc, curr) => acc + (specialAliquots[curr] || 0), 0);

        if (Math.abs(totalPercentage - 100) > 0.05) {
          Toast.fire({
            type: "error",
            title: "Error en distribución",
            text: `La suma de los porcentajes es ${totalPercentage.toFixed(4)}%. Debe ser exactamente 100%.`
          });
          return;
        }

        items = selectedProperties.map((id) => ({
          property_id: toPayloadPropertyId(id),
          amount: (totalValue * (specialAliquots[id] || 0)) / 100,
        }));
      }
    }

    const propertySelectionPayload = isAllSelectionMode
      ? {
        mode: "all",
        condominium_id: toPayloadPropertyId(condominiumId),
        filters: {},
        excluded_ids: excludedPropertyIds.map(toPayloadPropertyId),
      }
      : {
        mode: "manual",
        property_ids: selectedProperties.map(toPayloadPropertyId),
      };

    const submissionData = {
      ...data,
      // HO-TASK-260: una cuenta por pagar cargada desde el modulo de
      // propiedades es una deuda del propietario, no una compra a un
      // proveedor, asi que no se manda ninguno. `is_provider_common` tampoco
      // viaja: el backend (FinanceRequest) exige provider_id justo cuando
      // vale 1, y provider_name cuando vale 0.
      ...(fixedProperty
        ? { provider_id: null }
        : { is_provider_common: true }),
      value: formatAmountForBackend(
        distributionType === "fixed"
          ? totalValue * selectedPropertiesCount
          : distributionType === "equal"
            ? totalValue
            : data.value
      ),
      selected_properties_count: selectedPropertiesCount,
      property_selection: propertySelectionPayload,
      distribution_type: distributionType === "special_aliquot" ? "aliquot" : distributionType,
      properties: isAllSelectionMode
        ? null
        : items.map((item) => ({
            ...item,
            amount: formatAmountForBackend(item.amount),
          })),
      sub_expenses: null,
      period_id: data.period_id ? toPayloadPropertyId(data.period_id) : null,
      // HO-TASK-253: la tasa de la fecha del gasto en moneda local, se haya
      // cargado en bolívares o en dólares. Si no se pudo resolver va vacía y
      // el backend la completa con la registrada para esa fecha.
      rate: rateForExpenseDate || null,
    };

    const paymentError = expensePaymentError(payment, submissionData.value);
    if (paymentError) {
      Toast.fire({ type: "error", title: paymentError });
      return;
    }

    handleSubmitData({
      ...submissionData,
      ...expensePaymentPayload(payment, submissionData.value),
    });
  };

  const renderForm = () => {
    return (
      <>
        <FalconCardHeader title="Detalles del gasto" light={true} />
        <CardBody>
          <Row form>
            <Col sm>
              {fixedProperty ? (
                <FormGroup>
                  <Label>Propietario</Label>
                  <Input
                    disabled
                    value={`${fixedProperty.user?.data?.firstname} ${fixedProperty.user?.data?.lastname}`}
                  />
                </FormGroup>
              ) : (
                <>
                  <Label>Seleccionar Propietarios</Label>

                  <div className="d-flex flex-wrap align-items-center mb-2">
                    <Button
                      color={isAllPropertiesSelected ? "danger" : "primary"}
                      outline={!isAllPropertiesSelected}
                      size="sm"
                      className="mr-2 mb-2"
                      onClick={handleToggleSelectAllProperties}
                      disabled={!condominiumId}
                    >
                      {isAllPropertiesSelected
                        ? "Quitar selección global"
                        : "Seleccionar todos"}
                    </Button>
                    <small className="text-muted mb-2">
                      Seleccionados: {selectedPropertiesCount}
                      {totalPropertiesAvailable > 0
                        ? ` de ${totalPropertiesAvailable}`
                        : ""}
                    </small>
                  </div>

                  {isAllSelectionMode && (
                    <div className="alert alert-info py-2 px-3 fs--1 mb-3">
                      Selección global activa: se incluirán todos los propietarios del condominio
                      {excludedPropertyIds.length > 0
                        ? `, excepto ${excludedPropertyIds.length} desmarcado(s).`
                        : "."}
                    </div>
                  )}

                  {/* Search Input (Local filter for displayed page) */}
                  <div className="d-flex mb-3">
                    <Input
                      placeholder="Inmueble, nombre, usuario, cédula, teléfono o correo"
                      bsSize="sm"
                      value={searchText}
                      onKeyDown={(event) => {
                        if (event.key !== "Enter") return;
                        event.preventDefault();
                        applyPropertySearch(event.currentTarget.value);
                      }}
                      onChange={(event) => {
                        const value = event.target.value;
                        setSearchText(value);
                        if (value.trim() === "") applyPropertySearch("");
                      }}
                      className="mr-2"
                    />
                    <Button color="falcon-default" size="sm" className="text-nowrap" onClick={() => applyPropertySearch()}>
                      <FontAwesomeIcon icon={faSearch} className="mr-1" />
                      Buscar
                    </Button>
                  </div>

                  {/* Selected Summary Chips */}
                  {!isAllSelectionMode && selectedPropertiesDetails.length > 0 && (
                    <div className="mb-3 p-2 bg-light rounded border border-200">
                      <small className="d-block text-muted mb-2 font-weight-bold">
                        {selectedPropertiesDetails.length} Seleccionado(s):
                      </small>
                      <div className="d-flex flex-wrap">
                        {selectedPropertiesDetails.map((p) => (
                          <div
                            key={p.id}
                            className="badge badge-soft-primary mr-1 mb-1 p-2 d-flex align-items-center"
                          >
                            <span className="mr-2">
                              {`${p?.user?.data?.firstname || "Propiedad"} ${p?.user?.data?.lastname || `#${normalizePropertyId(p.id)}`}`.trim()}
                            </span>
                            <span
                              className="cursor-pointer text-hover-danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePropertySelect(p);
                              }}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handlePropertySelect(p);
                                }
                              }}
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div
                    style={{
                      maxHeight: "400px",
                      overflowY: "auto",
                      paddingRight: "5px",
                    }}
                  >
                    {renderPropertiesItems()}
                    {filteredProperties.length === 0 && (
                      <div className="text-center p-3 text-muted font-italic">
                        No se encontraron propietarios
                      </div>
                    )}
                  </div>

                  <div className="mt-3">
                    <FullPagination
                      nextPage={next}
                      prevPage={prev}
                      goToPage={goPa}
                      meta={met}
                      className={"shadow-none border-top-0"}
                      posText={"propietario"}
                      perPage={per}
                    />
                  </div>

                  {!hasSelectedProperties && (
                    <span
                      className={classNames(
                        "text-danger d-inline-block fs--1 font-weight-normal d-block mt-2"
                      )}
                    >
                      Debes seleccionar al menos un propietario
                    </span>
                  )}
                </>
              )}
            </Col>
          </Row>

          {selectedPropertiesCount > 1 && (
            <FormGroup className="mt-3 bg-white p-3 border rounded">
              <Label className="font-weight-bold">
                Método de distribución del gasto
              </Label>
              <div>
                <CustomInput
                  type="radio"
                  id="dist_equal"
                  name="distributionType"
                  label="Dividir monto total equitativamente"
                  checked={distributionType === "equal"}
                  onChange={() => setDistributionType("equal")}
                  className="mb-2"
                />
                <CustomInput
                  type="radio"
                  id="dist_fixed"
                  name="distributionType"
                  label="Cobro fijo por propiedad (El monto ingresado se cobrará a CADA uno)"
                  checked={distributionType === "fixed"}
                  onChange={() => setDistributionType("fixed")}
                  className="mb-2"
                />
                {!isAllSelectionMode &&
                  <CustomInput
                    type="radio"
                    id="dist_special_aliquot"
                    name="distributionType"
                    label="Alicuota Especial (Ingresar % manual)"
                    checked={distributionType === "special_aliquot"}
                    onChange={() => setDistributionType("special_aliquot")}
                    className="mb-2"
                  />
                }
              </div>
              {renderManualAmounts()}
            </FormGroup>
          )}

          <hr />

          {!fixedProperty && (
            <>
              <Row form>
                <Col md={12}>
                  <FormGroup>
                    <Label for="accounting_area_id">Cuenta / Área Contable</Label>
                    <Input
                      type="select"
                      name="accounting_area_id"
                      className={classNames({
                        "border-danger": errors.accounting_area_id,
                      })}
                      innerRef={register({
                        required: "Debes seleccionar un área contable.",
                      })}
                      defaultValue=""
                    >
                      <option value="" disabled>
                        Selecciona una Cuenta / Área
                      </option>
                      {expenseAccountingAreas.map((acc) => (
                        <option key={acc.id} value={acc.id}>
                          {acc.name}
                        </option>
                      ))}
                    </Input>
                    {errors.accounting_area_id && (
                      <WizardError
                        error={errors.accounting_area_id.message}
                        className="fs--1 font-weight-normal d-block"
                      />
                    )}
                    <Button
                      color="link"
                      size="sm"
                      className="p-0 mt-1"
                      onClick={() => setCreateAccountAreaModalOpen(true)}
                    >
                      <FontAwesomeIcon icon={faCog} className="mr-1" />
                      Nueva Área/Categoría
                    </Button>
                  </FormGroup>
                </Col>
              </Row>

              <Modal isOpen={createAccountAreaModalOpen} toggle={() => setCreateAccountAreaModalOpen(!createAccountAreaModalOpen)}>
                <div className="modal-header">
                  <h5 className="modal-title">Crear Nueva Área / Categoría de Gasto</h5>
                  <button type="button" className="close" onClick={() => setCreateAccountAreaModalOpen(false)}>
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <ModalBody>
                  <FormGroup>
                    <Label for="newAreaName">Nombre del Área (ej: Reparación Tubería)</Label>
                    <Input
                      type="text"
                      id="newAreaName"
                      value={newAreaName}
                      onChange={(e) => setNewAreaName(e.target.value)}
                      placeholder="Ingrese el nombre"
                    />
                  </FormGroup>
                  <div className="d-flex justify-content-end">
                    <Button color="secondary" className="mr-2" onClick={() => setCreateAccountAreaModalOpen(false)}>
                      Cancelar
                    </Button>
                    <Button color="primary" onClick={handleCreateAccountingArea}>
                      Crear Área
                    </Button>
                  </div>
                </ModalBody>
              </Modal>
            </>
          )}

          {fixedProperty && (
            <Row form>
              <Col md={12}>
                <FormGroup>
                  <Label for="period_id">
                    Período <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    name="period_id"
                    control={control}
                    rules={{ required: "Debes seleccionar un período." }}
                    render={({ onChange, value }) => (
                      <PeriodSelect
                        ref={periodSelectRef}
                        inputId="period_id"
                        condominiumId={condominiumId}
                        value={value}
                        onChange={onChange}
                        error={!!errors.period_id}
                        disableGeneratedPlanilla
                      />
                    )}
                  />
                  {errors.period_id && (
                    <WizardError
                      error={errors.period_id.message}
                      className="fs--1 font-weight-normal d-block"
                    />
                  )}
                  <Button
                    color="link"
                    size="sm"
                    className="p-0 mt-1"
                    onClick={handleOpenCreatePeriodModal}
                  >
                    <FontAwesomeIcon icon={faCog} className="mr-1" />
                    Nuevo Período
                  </Button>
                  <small className="d-block text-muted mt-1">
                    El saldo se asignará a este período en vez de calcularse
                    automáticamente. Los períodos con la planilla ya generada
                    no se pueden usar para un saldo inicial nuevo.
                  </small>
                </FormGroup>
              </Col>
            </Row>
          )}

          <Modal
            isOpen={createPeriodModalOpen}
            toggle={() => setCreatePeriodModalOpen(!createPeriodModalOpen)}
          >
            <ModalHeader toggle={() => setCreatePeriodModalOpen(false)}>
              Crear Nuevo Período
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <Label>Tipo de período</Label>
                <Input
                  type="select"
                  value={newPeriodIsCustom ? "custom" : "month"}
                  onChange={handlePeriodModalTypeChange}
                >
                  <option value="month">Mes del calendario</option>
                  <option value="custom">Personalizado</option>
                </Input>
              </FormGroup>
              {!newPeriodIsCustom ? (
                <FormGroup>
                  <Label>Mes</Label>
                  <Input
                    type="select"
                    value={newPeriodName}
                    onChange={handlePeriodModalMonthChange}
                  >
                    {periodMonthNames.map((monthName) => (
                      <option
                        key={monthName}
                        value={`${monthName} ${periodCurrentYear}`}
                      >
                        {`${monthName} ${periodCurrentYear}`}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              ) : (
                <FormGroup>
                  <Label>Nombre del período</Label>
                  <Input
                    value={newPeriodName}
                    onChange={(e) => setNewPeriodName(e.target.value)}
                    placeholder="Ej: Saldo inicial 2026"
                  />
                </FormGroup>
              )}
              <Row form>
                <Col sm={6}>
                  <FormGroup>
                    <Label>Fecha inicio</Label>
                    <Input
                      type="date"
                      value={newPeriodStartDate}
                      onChange={(e) => setNewPeriodStartDate(e.target.value)}
                    />
                  </FormGroup>
                </Col>
                <Col sm={6}>
                  <FormGroup>
                    <Label>Fecha fin</Label>
                    <Input
                      type="date"
                      value={newPeriodEndDate}
                      onChange={(e) => setNewPeriodEndDate(e.target.value)}
                    />
                  </FormGroup>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button
                color="secondary"
                onClick={() => setCreatePeriodModalOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                color="primary"
                onClick={handleCreatePeriod}
                disabled={creatingPeriod}
              >
                {creatingPeriod ? "Creando..." : "Crear Período"}
              </Button>
            </ModalFooter>
          </Modal>

          {/* HO-TASK-260: en el modulo de propiedades se le cobra al
          propietario, no se le compra a un proveedor. Ademas alli la lista de
          proveedores llega vacia, asi que el campo obligatorio dejaba el
          formulario sin manera de enviarse. */}
          {!fixedProperty && (
            <FormGroup>
              <Label for="provider_id">
                Proveedor <span className="text-danger">*</span>
              </Label>
              <Input
                id="provider_id"
                name="provider_id"
                type="select"
                innerRef={register({
                  required: "Debes seleccionar un proveedor.",
                })}
                className={classNames({
                  "border-danger": errors.provider_id,
                })}
              >
                <option value="">Selecciona un proveedor</option>
                {providerOptions.map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {provider.name}
                  </option>
                ))}
              </Input>
              {errors.provider_id && (
                <WizardError
                  error={errors.provider_id.message}
                  className="fs--1 font-weight-normal d-block"
                />
              )}
              <Button
                color="link"
                size="sm"
                className="p-0 mt-1"
                onClick={() => setCreateProviderModalOpen(true)}
              >
                <FontAwesomeIcon icon={faCog} className="mr-1" />
                Nuevo proveedor
              </Button>
            </FormGroup>
          )}

          <Modal isOpen={createProviderModalOpen} toggle={() => setCreateProviderModalOpen(false)}>
            <ModalBody>
              <CreateProviderForm
                handleSubmitData={handleCreateProviderSubmit}
                setRedirect={() => { }}
                setRedirectUrl={() => { }}
                onCancel={() => setCreateProviderModalOpen(false)}
              />
            </ModalBody>
          </Modal>
          <FormGroup>
            <Label for="name">Nombre</Label>
            <Input
              autoComplete="off"
              id="name"
              name="name"
              innerRef={register({
                required: "Nombre del gasto es requerido.",
                pattern: {
                  value: /^[a-zA-Z0-9 %\u00C0-\u00FF.,:;¡!¿?()\-_"']+$/i,
                  message: "Solo se aceptan alfanúmericos.",
                },
              })}
              placeholder="Ej: Reparación de tubería interna"
              className={classNames({
                "border-danger": errors.name?.message,
              })}
            />
            {errors.name && (
              <WizardError
                error={errors.name.message}
                className="fs--1 font-weight-normal d-block"
              />
            )}
          </FormGroup>
          <FormGroup>
            <Label for="description">Descripción </Label>
            <Input
              autoComplete="off"
              name="description"
              defaultValue={common?.description}
              innerRef={register({
                minLength: {
                  value: 4,
                  message: "La descripción debe tener al menos 4 carácteres.",
                },
              })}
              type={"textarea"}
              className={`${errors.description?.message ? "border-danger" : ""
                } form-control`}
              placeholder="Detalles adicionales..."
            />
            <WizardError
              error={errors.description?.message}
              className="fs--1 font-weight-normal d-block"
            />
          </FormGroup>
          <Row form>
            <Col sm={6} className="w-100">
              <FormGroup>
                <Label for="description">Fecha del gasto <span className="text-danger">*</span></Label>
                <Datetime
                  className={`${"w-100"} ${errors.expired_at?.message ? "border-danger" : ""
                    }`}
                  name="expired_at"
                  innerRef={register("expired_at", {
                    required: "La fecha del gasto es obligatoria.",
                  })}
                  value={watch("expired_at") || ""}
                  onChange={(d) => {
                    const formattedDate = moment(d).format(
                      "YYYY-MM-DD HH:mm:ss"
                    );

                    setValue("expired_at", formattedDate, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                  dateFormat="DD/MM/YYYY"
                  timeFormat={false}
                />

                <WizardError
                  error={errors.expired_at?.message}
                  className="fs--1 font-weight-normal d-block"
                />
              </FormGroup>
            </Col>
            <Col sm={6} tag={FormGroup}>
              <Label for="montoOriginal" className="font-weight-bold">
                {distributionType === "fixed" && selectedPropertiesCount > 1
                  ? "Monto a cobrar por CADA Propiedad"
                  : distributionType === "manual"
                    ? "Monto Total Calculado"
                    : "Monto Total del Gasto"}
              </Label>
              {distributionType === "manual" ? (
                <div
                  className="form-control bg-light font-weight-bold"
                  disabled
                >
                  {`${selectedCurrencyData?.iso || "$"} `}
                  {selectedProperties
                    .reduce((acc, curr) => acc + (manualAmounts[curr] || 0), 0)
                    .toFixed(2)}
                </div>
              ) : (
                <Controller
                  name="montoOriginal"
                  control={control}
                  rules={{ required: "Monto es requerido" }}
                  render={({ onChange, value }) => (
                    <>
                      <InputWithLeftSelect
                        selectValue={watchedOriginalCurrency || ""}
                        onSelectChange={handleCurrencyChange}
                        selectOptions={(currencyCatalog || []).map((item) => ({
                          value: String(item.id),
                          label: `${item.iso} (${item.name})`,
                        }))}
                        errorMessage={errors.montoOriginal?.message}
                        helpText={helpText}
                        selectAriaLabel="Moneda original"
                        selectMinWidth="120px"
                      >
                        <NumberFormat
                          customInput={Input}
                          thousandSeparator
                          prefix={
                            selectedCurrencyData?.iso
                              ? `${selectedCurrencyData.iso} `
                              : ""
                          }
                          fixedDecimalScale
                          decimalScale={2}
                          onValueChange={(vals) => onChange(vals.value)}
                          value={value}
                          placeholder="0.00"
                          className={classNames("font-weight-bold border-left-0", {
                            "border-danger": errors.montoOriginal,
                          })}
                        />
                      </InputWithLeftSelect>
                      {Boolean(parsedExpenseDate && parsedExpenseDate.isValid()) && isRateLoading && (
                        <small className="text-muted d-block mt-1">
                          <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
                          Consultando tasa para la fecha...
                        </small>
                      )}
                      {!isSelectedCurrencyUsd && rateResolution?.isFallback && !isRateLoading && (
                        <Alert color="warning" className="mt-2 mb-0 py-2 fs--1">
                          No se encontró la tasa oficial registrada para la fecha{" "}
                          <strong>{moment(rateResolution.selectedDateKey).format("DD/MM/YYYY")}</strong>.
                          {canRegisterRate ? (
                            <>
                              {" "}
                              <Button
                                color="link"
                                className="p-0 fs--1 font-weight-bold text-decoration-none ml-1"
                                onClick={() => setIsRateModalOpen(true)}
                              >
                                Registrar tasa
                              </Button>
                            </>
                          ) : (
                            " Se está utilizando una tasa de referencia."
                          )}
                        </Alert>
                      )}
                    </>
                  )}
                />
              )}
            </Col>
          </Row>

          {Boolean(watchedOriginalAmount) && (
            <Card className="border-warning bg-soft-warning mb-3">
              <CardBody className="py-3">
                <div className="d-flex align-items-start">
                  <div>
                    <div className="text-700 fs--1">
                      El monto que se guardará en el sistema será el equivalente en {getCurrencyName(baseCurrency.iso)} {baseCurrency.symbol}.
                      El valor mostrado arriba es solo la referencia original ingresada por el usuario.
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {!commonCost && !fixedProperty && Number(state?.type_id) !== 5 && (
            <ExpensePaymentFields
              condominiumId={id}
              amount={paymentAmount}
              value={payment}
              onChange={setPayment}
            />
          )}

          {!commonCost && (
            <Row
              form
              className="align-items-center justify-content-center justify-content-md-end mt-4"
            >
              <ButtonIcon
                color="falcon-default"
                className="my-2 mx-0 mx-md-2 m-md-0 px-5 mr-2"
                icon={faUndo}
                iconAlign="right"
                transform="down-1 shrink-4"
                onClick={() => {
                  if (onCancel) {
                    onCancel();
                  } else if (fixedProperty) {
                    history.goBack();
                  } else {
                    history.push({
                      pathname: `/dashboard/${moduleBase}/${id}/common-costs`,
                      state: {
                        type_id: state?.type_id,
                        type_name: state?.type_name,
                      },
                    });
                  }
                }}
              >
                Volver
              </ButtonIcon>
              <ButtonIcon
                data-testid="submit-button"
                color="primary"
                className="my-2 mx-0 mx-md-2 m-md-0 px-5"
                type="submit"
                icon="save"
                iconAlign="right"
                onClick={handleSubmit(onSubmit)}
                disabled={isSubmitting || isRateLoading}
                /*                 disabled={Object.keys(errors).length > 0} */
                transform="down-1 shrink-4"
              ></ButtonIcon>
            </Row>
          )}
        </CardBody>

      </>
    );
  };

  return (
    <Card
      className="bg-light mb-3"
      data-testid="form"
      tag={Form}
      onSubmit={handleSubmit(onSubmit)}
    >
      <PreloadState
        loading={isSubmitting}
        message={`Guardando ${state?.type_name || "gasto particular"}...`}
      >
        {renderForm()}
      </PreloadState>
      <CreateExhangeRateModal
        isOpen={isRateModalOpen}
        setIsOpen={setIsRateModalOpen}
        currency={selectedCurrencyData || { name: "", iso: "", id: null }}
        baseCurrency={baseCurrency}
        mutate={bumpRateRefreshTick}
        condominiumId={condominiumId || selectedCondominium}
        suggestedValue={activeExchangeRate}
      />
    </Card>
  );
};

export default withRedirect(CreateIndividualCostsForm);
